/**
 * The merchant's own dock preference, across reloads (MAR-207).
 *
 * **Width and collapsed state only.** ⚠️ The ask-entity pin must NEVER be
 * persisted: a restored pin would have the dock claiming to read a record whose
 * drawer is not open, which is the exact failure PR 1 spent three review rounds
 * killing. Nor is the *derived* auto-collapse persisted — only what the
 * merchant themselves chose — or a drawer left open at quit time would come
 * back as a collapse they never asked for.
 *
 * ⚠️ Every read and write is wrapped. `localStorage` throws in a private
 * window, with site data blocked, and inside a thumbnail capture; it can also
 * come back empty. Nothing here may throw, and the dock must render correctly
 * when it returns nothing.
 */

/** The design's default dock width (`Prototype - Three Surfaces v6`). */
export const DOCK_DEFAULT_WIDTH = 400
/** The grip's clamp, from the design's own `Math.min(640, Math.max(320, …))`. */
export const DOCK_MIN_WIDTH = 320
export const DOCK_MAX_WIDTH = 640

export const DOCK_PREF_KEY = 'marva.dock'
/** Bump to retire a stored shape. A mismatch is ignored, not migrated. */
export const DOCK_PREF_VERSION = 1

/**
 * The merchant's explicit collapse choice, **carrying the layout it was made
 * in**.
 *
 * ⚠️ The band tag belongs to the CHOICE, not to the moment of writing. An
 * earlier version wrote the band that happened to be current when the effect
 * fired, which quietly destroyed a desktop preference the moment the dock was
 * opened on a phone, and could re-tag "expanded" as an overlay choice — so the
 * next phone load restored an expanded dock covering the screen.
 */
export interface DockChoice {
  collapsed: boolean
  overlay: boolean
}

export interface DockPreference {
  width: number
  /** The merchant's explicit choice, or `null` if they never made one. */
  choice: DockChoice | null
}

const NOTHING_STORED: DockPreference = { width: DOCK_DEFAULT_WIDTH, choice: null }

export function clampDockWidth(px: number): number {
  return Math.min(DOCK_MAX_WIDTH, Math.max(DOCK_MIN_WIDTH, px))
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null
}

/**
 * The stored preference, **validated at the read** — shape, version, types and
 * the width clamp. Anything it cannot vouch for degrades to the default rather
 * than being trusted.
 *
 * ⚠️ It does NOT scope the choice to the current layout; `useDockLayout` does
 * that, at its own read, because the live band is the hook's to know and
 * because the stored tag must survive being read under the other one. Reading
 * and scoping in the same step is what let the write destroy a preference it
 * had merely declined to apply.
 */
export function readDockPreference(): DockPreference {
  let raw: string | null | undefined
  try {
    raw = window.localStorage?.getItem(DOCK_PREF_KEY)
  } catch {
    return NOTHING_STORED
  }
  if (!raw) return NOTHING_STORED

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return NOTHING_STORED
  }
  if (!isRecord(parsed) || parsed.v !== DOCK_PREF_VERSION) return NOTHING_STORED

  const storedWidth = parsed.width
  const width =
    typeof storedWidth === 'number' && Number.isFinite(storedWidth)
      ? clampDockWidth(storedWidth)
      : DOCK_DEFAULT_WIDTH

  // A choice exists only if both halves are present: what they chose, and the
  // layout they chose it in. Either missing and there is no usable choice.
  const choice =
    typeof parsed.collapsed === 'boolean' && typeof parsed.overlay === 'boolean'
      ? { collapsed: parsed.collapsed, overlay: parsed.overlay }
      : null

  return { width, choice }
}

/** Store the merchant's choice. Silent on failure — this is a convenience. */
export function writeDockPreference(pref: DockPreference): void {
  try {
    window.localStorage?.setItem(
      DOCK_PREF_KEY,
      JSON.stringify({
        v: DOCK_PREF_VERSION,
        width: pref.width,
        // ⚠️ The choice's OWN band, never the band that happens to be current.
        collapsed: pref.choice ? pref.choice.collapsed : null,
        overlay: pref.choice ? pref.choice.overlay : null,
      })
    )
  } catch {
    // A private window, or blocked site data. The dock still works.
  }
}
